// src/components/team/TeamMemberDetailSlideOver.tsx
// Team member detail slide-over panel

import { useState } from 'react';
import { resolveUserInitials } from '@levelup/central-auth';
import SlideOver from '../ui/SlideOver';
import Button from '../ui/Button';
import { DeleteConfirmDialog } from '../ui/ConfirmDialog';
// CRM BFF (server-held Central token). The browser-direct Central members API
// this used cannot work in production RP/BFF mode — no Central token in the
// browser — so role change and removal silently failed there.
import {
  usePlatformUpdateMemberRoleMutation,
  usePlatformRemoveMemberMutation,
} from '../../api/platformMembersApi';
import { baseApi } from '../../api/baseApi';
import { useAppDispatch } from '../../store/store';
import MemberVisibilitySection from './MemberVisibilitySection';
import PersonRulesSection from '../workAccountability/PersonRulesSection';
import ProviderWorkloadPanel from '../providerAssignments/ProviderWorkloadPanel';
import SubjectComplianceSection from '../compliance/SubjectComplianceSection';
import { useHasCapability } from '../../services/centralContext';
import { useWorkAccountabilityEnabled } from '../../hooks/useWorkAccountability';
import {
  INVITABLE_ROLES,
  getBadgeClasses,
  getCrmRoleForCentralRole,
  getCentralRoleForCrmRole,
  type CentralRole,
  type CrmRoleKey,
} from '@levelup/crm-roles';
import type { TeamMemberSummary } from '../../types/api';

function formatElapsedTime(clockInAt: string): string {
  const start = new Date(clockInAt).getTime();
  const now = Date.now();
  const diff = Math.floor((now - start) / 1000);

  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

interface TeamMemberDetailSlideOverProps {
  member: TeamMemberSummary | null;
  onClose: () => void;
}

export default function TeamMemberDetailSlideOver({ member, onClose }: TeamMemberDetailSlideOverProps) {
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [updateRole, { isLoading: isUpdatingRole }] = usePlatformUpdateMemberRoleMutation();
  const [removeMember, { isLoading: isRemoving }] = usePlatformRemoveMemberMutation();
  const [actionError, setActionError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  // Hook must run unconditionally, before the early return below (rules of hooks).
  const waEnabled = useWorkAccountabilityEnabled();

  if (!member) return null;

  // `role` is nullable: the CRM team list serves `legacyRole`, which is NULL
  // when the membership carries no CRM relationship classification (e.g. it was
  // projected from a Central admin/member/viewer role — see
  // centralRoleToCrmRelationshipRole on the server). `?? ''` keeps the existing
  // defensive fallback in @levelup/crm-roles rather than throwing.
  const resolvedRole = getCrmRoleForCentralRole((member.role ?? '') as CentralRole);
  const badge = getBadgeClasses(member.role ?? '');
  const canManage = member.role !== 'owner';

  const handleRoleChange = async (crmRoleKey: CrmRoleKey) => {
    const { role: centralRole } = getCentralRoleForCrmRole(crmRoleKey);
    if (centralRole === member.role) return;
    setActionError(null);
    try {
      // Workspace is the session's (BFF) — never sent from the browser.
      await updateRole({
        userId: member.userId,
        role: centralRole as 'admin' | 'member' | 'viewer',
      }).unwrap();
      // The Team page list is cached under 'Team' too; refresh it.
      dispatch(baseApi.util.invalidateTags(['Team']));
    } catch (error) {
      console.error('Failed to update role:', error);
      setActionError('Could not change the role. Please try again.');
    }
  };

  const handleRemove = async () => {
    setActionError(null);
    try {
      await removeMember({ userId: member.userId }).unwrap();
      dispatch(baseApi.util.invalidateTags(['Team']));
      setShowRemoveConfirm(false);
      onClose();
    } catch (error) {
      console.error('Failed to remove member:', error);
      setShowRemoveConfirm(false);
      setActionError('Could not remove this member. Please try again.');
    }
  };

  return (
    <SlideOver
      isOpen={!!member}
      onClose={onClose}
      title="Team Member"
      size="md"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-6">
        {actionError && (
          <div role="alert" className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">
            {actionError}
          </div>
        )}

        {/* Member Header */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            {member.avatarUrl ? (
              <img
                src={member.avatarUrl}
                alt=""
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <span className="text-2xl font-medium text-blue-600">
                {resolveUserInitials({ fullName: member.fullName, email: member.email })}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-semibold text-gray-900 truncate">
              {member.fullName || 'No name'}
            </h3>
            <p className="text-sm text-gray-500 truncate">{member.email}</p>
          </div>
        </div>

        {/* Status Section */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Current Status</h4>
          {member.isClockedIn ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="font-medium text-green-700">Currently Working</span>
              </div>
              {member.clockInAt && (
                <span className="text-sm text-gray-600">
                  {formatElapsedTime(member.clockInAt)} elapsed
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-gray-400 rounded-full" />
              <span className="font-medium text-gray-600">Not Working</span>
            </div>
          )}
          {member.isClockedIn && member.clockInAt && (
            <p className="text-xs text-gray-500 mt-2">
              Clocked in at {new Date(member.clockInAt).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })}
            </p>
          )}
        </div>

        {/* Role Section */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Role</h4>
          {canManage ? (
            <div>
              <select
                value={resolvedRole.key}
                onChange={(e) => handleRoleChange(e.target.value as CrmRoleKey)}
                disabled={isUpdatingRole}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
              >
                {INVITABLE_ROLES.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">{resolvedRole.shortDescription}</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-3 py-1.5 text-sm font-medium rounded-full ${badge.bg} ${badge.text}`}
                >
                  {badge.label}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">{resolvedRole.shortDescription}</p>
            </div>
          )}
        </div>

        {/* Contact Information */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Contact</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500">Email</p>
                <a
                  href={`mailto:${member.email}`}
                  className="text-sm text-blue-600 hover:underline truncate block"
                >
                  {member.email}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Visibility Assignments */}
        <MemberVisibilitySection
          memberId={member.userId}
          workspaceId={member.workspaceId}
          canManage={canManage}
        />

        {/* Work Accountability person rules (PR-E) — flag-gated. */}
        {waEnabled && (
          <div className="pt-4 border-t border-gray-200">
            <PersonRulesSection workspaceId={member.workspaceId} userId={member.userId} canManage={canManage} />
          </div>
        )}

        {/* Provider workload — this user's active Provider Assignments. */}
        <ProviderWorkloadPanel
          providerUserId={member.userId}
          workspaceId={member.workspaceId}
        />

        {/* Compliance eligibility — backend-evaluated. */}
        <TeamMemberComplianceBlock
          userId={member.userId}
          workspaceId={member.workspaceId}
        />

        {/* Member Info */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Details</h4>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">User ID</dt>
              <dd className="text-gray-900 font-mono text-xs truncate max-w-[200px]">{member.userId}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Workspace ID</dt>
              <dd className="text-gray-900 font-mono text-xs truncate max-w-[200px]">{member.workspaceId}</dd>
            </div>
          </dl>
        </div>

        {/* Remove Member */}
        {canManage && (
          <div className="pt-4 border-t border-gray-200">
            <Button
              variant="ghost"
              onClick={() => setShowRemoveConfirm(true)}
              className="w-full text-red-600 hover:bg-red-50"
              disabled={isRemoving}
            >
              {isRemoving ? 'Removing...' : 'Remove from Workspace'}
            </Button>
          </div>
        )}
      </div>

      {/* Remove Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={showRemoveConfirm}
        itemName={member.fullName || member.email}
        itemType="team member"
        onConfirm={handleRemove}
        onCancel={() => setShowRemoveConfirm(false)}
      />
    </SlideOver>
  );
}

// ---------------------------------------------------------------------------
// TeamMemberComplianceBlock — capability-gated
// ---------------------------------------------------------------------------

function TeamMemberComplianceBlock({
  userId,
  workspaceId,
}: {
  userId: string;
  workspaceId: string;
}) {
  const canView = useHasCapability('compliance', 'view');
  if (!canView) return null;
  return (
    <div className="pt-4 border-t border-gray-200">
      <h4 className="text-sm font-medium text-gray-700 mb-3">Compliance</h4>
      <SubjectComplianceSection
        workspaceId={workspaceId}
        subjectType="provider_user"
        subjectId={userId}
      />
    </div>
  );
}
